import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, ChevronLeft, Clock, Users, Check,
  X, ChevronLeft as Prev, ChevronRight as Next,
  Star, Quote, Award
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { internshipPrograms } from '../data/internships';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-white hover:text-orange-400 transition-colors z-10">
          <X className="w-8 h-8" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-6 top-1/2 -translate-y-1/2 text-white hover:text-orange-400 bg-white/10 rounded-full p-3 transition-all hover:bg-white/20 z-10">
          <Prev className="w-6 h-6" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-white hover:text-orange-400 bg-white/10 rounded-full p-3 transition-all hover:bg-white/20 z-10">
          <Next className="w-6 h-6" />
        </button>

        <motion.div
          key={index}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="max-w-4xl max-h-[80vh] mx-10"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={photos[index].url} alt={photos[index].caption}
            className="w-full max-h-[70vh] object-contain rounded-2xl shadow-2xl"
          />
          <p className="text-center text-slate-300 text-sm mt-4">{photos[index].caption}</p>
          <p className="text-center text-slate-500 text-xs mt-1">{index + 1} / {photos.length}</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function InternshipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const prog = internshipPrograms.find((p) => p.id === id);

  const [lightboxIndex, setLightboxIndex] = useState(null);

  if (!prog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060D1F]">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-white mb-2">Program Not Found</h2>
          <button onClick={() => navigate('/internships')} className="text-orange-400 font-semibold mt-4 inline-flex items-center gap-2">
            <ChevronLeft className="w-4 h-4" /> Back to Programs
          </button>
        </div>
      </div>
    );
  }

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + prog.photos.length) % prog.photos.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % prog.photos.length);

  return (
    <div className="min-h-screen bg-[#060D1F]">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 bg-[#060D1F] overflow-hidden">
        <div className="absolute top-10 right-10 w-96 h-96 bg-orange-500/15 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '36px 36px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3 mb-6">
              <span className={`${prog.iconBg} text-white text-xs font-bold px-3 py-1.5 rounded-lg`}>{prog.level}</span>
              <span className="bg-white/10 border border-white/20 text-white/70 text-xs px-3 py-1.5 rounded-lg">{prog.batch}</span>
              <span className="bg-white/10 border border-white/20 text-white/70 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1">
                <Clock className="w-3 h-3" /> {prog.duration}
              </span>
              <span className="bg-white/10 border border-white/20 text-white/70 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1">
                <Users className="w-3 h-3" /> {prog.studentsCount} interns
              </span>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
                  {prog.title}
                </motion.h1>
                <motion.p variants={fadeUp} className="text-slate-400 text-lg leading-relaxed mb-8">
                  {prog.description}
                </motion.p>
                <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                  <button
                    onClick={() => navigate('/')}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 rounded-xl font-semibold flex items-center gap-2 transition-colors"
                  >
                    Apply Now <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate('/internships')}
                    className="border border-white/20 text-white hover:bg-white/5 px-7 py-3.5 rounded-xl font-semibold transition-colors"
                  >
                    Other Programs
                  </button>
                </motion.div>
              </div>

              <motion.div
                variants={fadeUp}
                className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10"
              >
                <img src={prog.coverImage} alt={prog.title} className="w-full h-64 lg:h-72 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                  {prog.skills.map((sk) => (
                    <span key={sk} className="bg-black/40 backdrop-blur-sm border border-white/20 text-white text-xs px-2.5 py-1 rounded-lg">{sk}</span>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-16 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {prog.highlights.map((h, i) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/20 rounded-2xl p-4"
              >
                <div className="w-8 h-8 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-slate-300 font-medium text-sm">{h}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY TIMELINE */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Program Journey</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-bold text-white mt-3">
              Week-by-Week <span className="text-orange-500">Breakdown</span>
            </motion.h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-white/15 -translate-x-1/2" />

            {prog.milestones.map((m, i) => (
              <motion.div
                key={m.week}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className={`relative flex gap-6 md:gap-0 mb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`flex-1 md:px-10 pl-14 md:pl-0 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="bg-[#0C1628] rounded-2xl p-6 border border-white/10 hover:border-orange-500/30 transition-all duration-300">
                    <div className="inline-block bg-orange-500/15 text-orange-400 text-xs font-bold px-3 py-1 rounded-full mb-3">
                      {m.week}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{m.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{m.description}</p>
                  </div>
                </div>

                <div className="absolute left-6 md:left-1/2 top-6 w-4 h-4 bg-orange-500 rounded-full border-4 border-[#060D1F] shadow-md -translate-x-1/2 z-10" />

                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO GALLERY */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Photo Gallery</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-bold text-white mt-3">
              Moments from the <span className="text-orange-500">Journey</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3">Click any photo to view fullscreen</motion.p>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
          >
            {prog.photos.map((photo, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{ scale: 1.02 }}
                onClick={() => openLightbox(i)}
                className={`relative overflow-hidden rounded-2xl cursor-pointer group ${i === 0 ? 'col-span-2 row-span-1' : ''}`}
              >
                <img
                  src={photo.url} alt={photo.caption}
                  className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white font-semibold text-sm bg-black/50 px-4 py-2 rounded-xl">
                    {photo.caption}
                  </span>
                </div>
                <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  {i + 1}/{prog.photos.length}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PROJECTS BUILT */}
      <section className="py-20 bg-[#060D1F]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.span variants={fadeUp} className="text-orange-400 font-semibold text-sm uppercase tracking-widest">Projects Built</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-bold text-white mt-3">
              What Our Interns <span className="text-orange-500">Shipped</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {prog.projects.map((proj, i) => (
              <motion.div
                key={proj.name}
                variants={fadeUp}
                whileHover={{ y: -6, borderColor: 'rgba(249,115,22,0.4)' }}
                className="bg-[#0C1628] border border-white/10 rounded-2xl p-6 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-orange-500/20 border border-orange-500/30 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{proj.name}</h3>
                <p className="text-slate-400 text-sm mb-4 leading-relaxed">{proj.description}</p>
                <div className="flex flex-wrap gap-2">
                  {proj.tech.map((t) => (
                    <span key={t} className="bg-white/5 border border-white/10 text-slate-300 text-xs px-2.5 py-1 rounded-lg">{t}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-[#080F1E]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.span variants={fadeUp} className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Testimonials</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-bold text-white mt-3">
              Straight from our <span className="text-orange-500">Alumni</span>
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-6">
            {prog.testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-[#0C1628] border border-white/10 rounded-2xl p-7 relative hover:border-orange-500/30 transition-all duration-300"
              >
                <Quote className="w-8 h-8 text-orange-500/30 absolute top-6 right-6" />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-orange-400 fill-orange-400" />)}
                </div>
                <p className="text-slate-300 leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/15 flex items-center justify-center text-orange-400 font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="font-bold text-white">{t.name}</div>
                    <div className="text-orange-400 text-xs font-medium">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* APPLY CTA */}
      <section className="py-20 bg-[#060D1F] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/15 rounded-full blur-[80px]" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Interested in <span className="text-orange-400">{prog.title}?</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 text-lg mb-8">
              Join our next batch. Limited seats available. Apply today.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-2 transition-colors"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/internships')}
                className="border border-white/20 text-white hover:bg-white/5 px-8 py-4 rounded-xl font-semibold transition-colors"
              >
                View Other Programs
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {lightboxIndex !== null && (
        <Lightbox
          photos={prog.photos}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </div>
  );
}
